import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { Platform, Pressable, TextInput, View } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import "../../theme";
import { Icon } from "../Icon";
import { Text } from "../Text";
import type { InputProps, InputSize } from "./types";

// ---------------------------------------------------------------------------
// Layout constants
// ---------------------------------------------------------------------------

const LABEL_LEFT = 14;
const NOTCH_PAD = 4;
const BORDER_RADIUS = 14;
const TRANSITION_MS = 250;
const LABEL_HIDE_DELAY_MS = 80;

const SIZE_HEIGHT: Record<InputSize, number> = {
  small: 36,
  medium: 44,
};

// ---------------------------------------------------------------------------
// Input — the generic text field atom shared by SearchInput, Autocomplete,
// and the date pickers. Provides a notched floating label, focus/error border
// states, optional clear button, and read-only "trigger" usage.
// ---------------------------------------------------------------------------

const Input: FC<InputProps> = ({
  value,
  onChangeText,
  placeholder,
  label,
  error,
  helperText,
  disabled = false,
  editable = true,
  size = "medium",
  active = false,
  clearable = false,
  onClear,
  leftContent,
  rightContent,
  onPress,
  onFocus,
  onBlur,
  onKeyPress,
  textInputProps,
  accessibilityLabel,
  clearAccessibilityLabel = "Clear",
}) => {
  const { theme } = useUnistyles();
  const [isFocused, setIsFocused] = useState(false);

  const isEditable = editable && !disabled;
  const isActive = isFocused || active;
  const hasError = Boolean(error);
  // Clear is available whenever there is a value, including read-only trigger
  // usage (e.g. date pickers) where the field itself is not editable.
  const showClear = clearable && !disabled && value.length > 0;

  const hasContent = value.length > 0 || leftContent != null;
  const shouldShowLabel = Boolean(label) && (hasContent || isActive);

  const [labelVisible, setLabelVisible] = useState(shouldShowLabel);
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (shouldShowLabel) {
      if (blurTimerRef.current) {
        clearTimeout(blurTimerRef.current);
        blurTimerRef.current = null;
      }
      setLabelVisible(true);
    } else {
      blurTimerRef.current = setTimeout(() => {
        setLabelVisible(false);
      }, LABEL_HIDE_DELAY_MS);
    }
    return () => {
      if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    };
  }, [shouldShowLabel]);

  // Remove the default web focus outline (the border itself indicates focus).
  const inputRef = useCallback((node: TextInput | null) => {
    if (Platform.OS === "web" && node) {
      (node as unknown as HTMLElement).style.outline = "none";
    }
  }, []);

  const handleClear = useCallback(() => {
    if (onClear) {
      onClear();
      return;
    }
    onChangeText?.("");
  }, [onClear, onChangeText]);

  const {
    onFocus: consumerOnFocus,
    onBlur: consumerOnBlur,
    onKeyPress: consumerOnKeyPress,
    style: consumerStyle,
    ...restTextInputProps
  } = textInputProps ?? {};

  const borderColor =
    hasError ? theme.colors.status.error
    : isActive ? theme.colors.interactive.controls.focused_border_outline
    : theme.colors.field.border_default;

  const transitionStyle =
    Platform.OS === "web" ?
      ({
        transition: `opacity ${TRANSITION_MS.toString()}ms ease, transform ${TRANSITION_MS.toString()}ms ease`,
      } as Record<string, string>)
    : undefined;

  const height = SIZE_HEIGHT[size];

  // The text input is wrapped so trigger usage (editable=false + onPress) does
  // not let the input swallow the press.
  const textNode = (
    <TextInput
      {...restTextInputProps}
      ref={inputRef}
      value={value}
      editable={isEditable}
      onChangeText={onChangeText}
      onFocus={(e) => {
        setIsFocused(true);
        onFocus?.();
        consumerOnFocus?.(e);
      }}
      onBlur={(e) => {
        setIsFocused(false);
        onBlur?.();
        consumerOnBlur?.(e);
      }}
      onKeyPress={(e) => {
        onKeyPress?.(e);
        consumerOnKeyPress?.(e);
      }}
      placeholder={labelVisible && value ? undefined : placeholder}
      placeholderTextColor={theme.colors.textMuted}
      pointerEvents={isEditable ? "auto" : "none"}
      style={[styles.input, leftContent != null && styles.inputWithLeftContent, consumerStyle]}
    />
  );

  const fieldBody = (
    <View style={[styles.container, { minHeight: height }, disabled && styles.disabled]}>
      {label ?
        <>
          <View
            style={[styles.solidBorder, { borderColor, opacity: labelVisible ? 0 : 1 }, transitionStyle]}
            pointerEvents="none"
          />
          <View style={[styles.notchedBorder, { opacity: labelVisible ? 1 : 0 }, transitionStyle]} pointerEvents="none">
            <View style={[styles.notchLeading, { borderColor, width: LABEL_LEFT }]} />
            <View style={[styles.notchGap, { borderColor }]}>
              <View
                style={[
                  { opacity: labelVisible ? 1 : 0, transform: [{ translateY: labelVisible ? 0 : 4 }] },
                  transitionStyle,
                ]}
              >
                <Text style={[styles.label, { color: borderColor }]}>{label}</Text>
              </View>
            </View>
            <View style={[styles.notchTrailing, { borderColor }]} />
          </View>
        </>
      : <View style={[styles.solidBorder, { borderColor }]} pointerEvents="none" />}

      <View style={[styles.inputRow, { minHeight: height }]}>
        {leftContent}
        {textNode}
        {(showClear || rightContent != null) && (
          <View style={styles.rightCluster}>
            {showClear && (
              <Pressable
                onPress={handleClear}
                hitSlop={8}
                accessibilityRole="button"
                accessibilityLabel={clearAccessibilityLabel}
              >
                <Icon name="closeCircle" size={18} color={theme.colors.textMuted} />
              </Pressable>
            )}
            {rightContent}
          </View>
        )}
      </View>
    </View>
  );

  return (
    <View>
      {onPress ?
        <Pressable
          onPress={disabled ? undefined : onPress}
          accessibilityState={{ disabled }}
          accessibilityLabel={accessibilityLabel ?? label}
        >
          {fieldBody}
        </Pressable>
      : fieldBody}

      {(hasError || helperText != null) && (
        <Text textStyle="xs/regular" color={hasError ? "danger" : "textMuted"} style={styles.helperText}>
          {error ?? helperText}
        </Text>
      )}
    </View>
  );
};

Input.displayName = "Input";

export { Input };

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create((theme) => ({
  container: {
    position: "relative",
    justifyContent: "center",
    backgroundColor: theme.colors.surface,
    borderRadius: BORDER_RADIUS,
  },
  disabled: {
    opacity: 0.5,
  },
  inputRow: {
    flexDirection: "row",
    alignItems: "center",
    overflow: "hidden",
  },
  solidBorder: {
    ...StyleSheet.absoluteFillObject,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS,
  },
  notchedBorder: {
    ...StyleSheet.absoluteFillObject,
    flexDirection: "row",
    alignItems: "stretch",
  },
  notchLeading: {
    borderLeftWidth: 1,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderTopLeftRadius: BORDER_RADIUS,
    borderBottomLeftRadius: BORDER_RADIUS,
  },
  notchGap: {
    borderBottomWidth: 1,
    justifyContent: "flex-start",
    paddingHorizontal: NOTCH_PAD,
    marginTop: -8,
  },
  notchTrailing: {
    flex: 1,
    borderRightWidth: 1,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderTopRightRadius: BORDER_RADIUS,
    borderBottomRightRadius: BORDER_RADIUS,
  },
  label: {
    fontSize: 11,
    fontWeight: "500",
    lineHeight: 16,
  },
  input: {
    flex: 1,
    minWidth: 0,
    paddingHorizontal: theme.spacing["4xl"],
    color: theme.colors.text,
    fontSize: 14,
  },
  inputWithLeftContent: {
    paddingLeft: theme.spacing.sm,
  },
  rightCluster: {
    flexDirection: "row",
    alignItems: "center",
    flexShrink: 0,
    gap: theme.spacing.md,
    paddingRight: theme.spacing["3xl"],
  },
  helperText: {
    marginTop: theme.spacing.xs,
    marginLeft: theme.spacing.md,
  },
}));
